import React, { useEffect, useState } from "react";
import { Plus, SaveIcon, SquarePen, X } from "lucide-react";

import ButtonV2 from "../../button/v2";

export type MultiValueEditableFieldType = "textarea" | "email" | "tel";

export interface MultiValueItem {
  id: string;
  value: any;
}

export interface MultiValueEditableFieldProps {
  label: string;
  values: MultiValueItem[];
  type: MultiValueEditableFieldType;
  isEditable?: boolean;
  disabled?: boolean;
  onSave?: (id: string | undefined, value: any) => Promise<boolean>;
  isEditing?: boolean;
  onEditStart?: (id?: string) => void;
  onEditCancel?: () => void;
}

const formatPhoneDisplay = (val: string): string => {
  if (!val) return "--";
  const digits = String(val).replace(/\D/g, "");
  return digits.length === 10 ? `+91 ${digits.slice(0, 5)} ${digits.slice(5)}` : val;
};

const MultiValueEditableField: React.FC<MultiValueEditableFieldProps> = ({
  label,
  values = [],
  type,
  isEditable = true,
  disabled = false,
  onSave,
  isEditing = false,
  onEditStart,
  onEditCancel,
}) => {
  const [activeId, setActiveId] = useState<string | undefined>(undefined);
  const [editValue, setEditValue] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);

  const inputPlaceholder = `Enter ${label.toLowerCase()}`;

  const formatInitialEditValue = (id?: string) => {
    if (!id) return "";
    const targetItem = values.find((item) => item.id === id);
    if (!targetItem?.value) return "";
    const valStr = String(targetItem.value);
    return type === "tel" ? valStr.replace(/\D/g, "").slice(0, 10) : valStr;
  };

  useEffect(() => {
    if (!isEditing) {
      setActiveId(undefined);
      setEditValue("");
      setInputError(null);
    }
  }, [values, isEditing]);

  const handleEdit = (id?: string) => {
    if (!isEditable || !onSave || disabled || isSaving) return;
    setActiveId(id);
    setEditValue(formatInitialEditValue(id));
    setInputError(null);
    onEditStart?.(id);
  };

  const handleCancel = () => {
    setEditValue(formatInitialEditValue(activeId));
    setInputError(null);
    onEditCancel?.();
  };

  const validateInput = (): string | null => {
    const trimmed = editValue.trim();
    if (trimmed === "") {
      if (type === "email") return "Please enter an email address";
      if (type === "tel") return "Please enter a 10-digit phone number";
      return `${label} cannot be empty`;
    }

    if (type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmed)) {
        return "Please enter a valid email address (e.g. name@domain.com)";
      }
    } else if (type === "tel") {
      const digits = trimmed.replace(/\D/g, "");
      if (digits.length !== 10) {
        return "Please enter a valid 10-digit phone number";
      }
    }
    return null;
  };

  const handleSave = async (id?: string) => {
    if (!onSave || disabled) return;

    const errorMsg = validateInput();
    if (errorMsg) {
      setInputError(errorMsg);
      return;
    }
    setInputError(null);

    setIsSaving(true);
    try {
      await onSave(id, editValue.trim());
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputError(null);
    setEditValue(type === "tel" ? e.target.value.replace(/\D/g, "") : e.target.value);
  };

  const formatValueItem = (val: string): React.ReactNode => {
    if (!val) return "--";
    return type === "tel" ? formatPhoneDisplay(val) : val;
  };

  const renderActionButtons = (id?: string) => {
    if (isSaving) {
      return <span className="shrink-0 text-xs font-medium italic text-[#71717A]">Saving...</span>;
    }

    return (
      <div className="flex shrink-0 items-center gap-1">
        <ButtonV2
          variant="ghost"
          className="h-4 w-4 shrink-0 p-0 text-[#71717A] hover:bg-transparent"
          onClick={() => void handleSave(id)}
          disabled={isSaving}
          prefix={<SaveIcon className="text-[#71717A] hover:text-[#0A0A0A]" width={14} height={14} />}
        />
        <ButtonV2
          variant="ghost"
          className="h-4 w-4 shrink-0 p-0 text-[#71717A] hover:bg-transparent"
          onClick={handleCancel}
          disabled={isSaving}
          prefix={<X className="text-[#71717A] hover:text-[#0A0A0A]" width={14} height={14} />}
        />
      </div>
    );
  };

  const renderInputControl = () => {
    if (type === "textarea") {
      return (
        <textarea
          autoFocus
          rows={3}
          placeholder={inputPlaceholder}
          value={editValue}
          onChange={handleInputChange}
          disabled={isSaving}
          className="w-full min-w-0 flex-1 resize-none border-none bg-transparent p-0 text-xs font-medium leading-4 text-[#0A0A0A] outline-none focus:ring-0"
        />
      );
    }

    if (type === "tel") {
      return (
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <span className="shrink-0 select-none text-xs font-medium leading-4 text-[#71717A]">+91</span>
          <input
            autoFocus
            type="tel"
            maxLength={10}
            value={editValue}
            onChange={handleInputChange}
            disabled={isSaving}
            placeholder="Enter 10-digit Phone"
            className="h-4 w-full min-w-0 flex-1 border-none bg-transparent p-0 text-xs font-medium leading-4 text-[#0A0A0A] outline-none focus:ring-0"
          />
        </div>
      );
    }

    return (
      <input
        autoFocus
        type="email"
        value={editValue}
        onChange={handleInputChange}
        disabled={isSaving}
        placeholder={inputPlaceholder}
        className="h-4 w-full min-w-0 flex-1 border-none bg-transparent p-0 text-xs font-medium leading-4 text-[#0A0A0A] outline-none focus:ring-0"
      />
    );
  };

  const renderEditBox = (id?: string) => (
    <div className="flex w-full flex-col">
      <div
        className={`flex min-w-0 flex-1 justify-between gap-2 rounded-md border border-[#818CF8] bg-white px-2 py-1 shadow-sm focus-within:ring-1 focus-within:ring-[#818CF8] ${
          type === "textarea" ? "items-start" : "items-center"
        }`}
      >
        {renderInputControl()}
        {renderActionButtons(id)}
      </div>
      {inputError && <p className="mt-1 text-xs font-normal leading-4 text-red-600">{inputError}</p>}
    </div>
  );

  return (
    <div className="flex w-full items-start gap-4">
      <div className="w-[180px] shrink-0 pt-1">
        <span className="text-xs font-medium leading-4 text-[#71717A]">{label}</span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {values.length > 0 ? (
          values.map((item, idx) => {
            const isEditingThisItem = isEditing && activeId === item.id;

            return (
              <div key={item.id || idx} className="w-full">
                {isEditingThisItem ? (
                  renderEditBox(item.id)
                ) : (
                  <div className={`group/val flex min-w-0 flex-1 justify-between gap-2 rounded-md border border-transparent px-2 py-1 transition-colors hover:bg-[#F3F4F6] ${
                      type === "textarea" ? "items-start" : "items-center"
                    }`}>
                    <span className="min-w-0 flex-1 text-xs font-medium leading-4 text-[#0A0A0A] whitespace-pre-wrap break-words">
                      {formatValueItem(item.value)}
                    </span>
                    <div
                      className={`flex h-4 w-4 shrink-0 items-center justify-end opacity-0 group-hover/val:opacity-100 ${
                        disabled || isSaving ? "cursor-not-allowed" : ""
                      }`}
                    >
                      {isEditable && onSave && (
                        <ButtonV2
                          variant="ghost"
                          className={`h-4 w-4 shrink-0 p-0 text-[#71717A] hover:bg-transparent ${
                            disabled || isSaving ? "cursor-not-allowed" : "hover:text-[#0A0A0A]"
                          }`}
                          onClick={() => handleEdit(item.id)}
                          disabled={disabled || isSaving}
                          prefix={
                            <SquarePen
                              className={`text-[#71717A] ${disabled || isSaving ? "opacity-80" : ""}`}
                              width={14}
                              height={14}
                            />
                          }
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : isEditing && activeId === undefined ? (
          renderEditBox(undefined)
        ) : (
          <div className="group/val flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md border border-transparent px-2 py-1 transition-colors hover:bg-[#F3F4F6]">
            <span className="min-w-0 flex-1 text-xs font-medium leading-4 text-[#0A0A0A]">--</span>
            <div
              className={`flex h-4 w-4 shrink-0 items-center justify-end opacity-0 group-hover/val:opacity-100 ${
                disabled || isSaving ? "cursor-not-allowed" : ""
              }`}
            >
              {isEditable && onSave && (
                <ButtonV2
                  variant="ghost"
                  className={`h-4 w-4 shrink-0 p-0 text-[#71717A] hover:bg-transparent ${
                    disabled || isSaving ? "cursor-not-allowed" : "hover:text-[#0A0A0A]"
                  }`}
                  onClick={() => handleEdit(undefined)}
                  disabled={disabled || isSaving}
                  prefix={
                    <SquarePen
                      className={`text-[#71717A] ${disabled || isSaving ? "opacity-80" : ""}`}
                      width={14}
                      height={14}
                    />
                  }
                />
              )}
            </div>
          </div>
        )}

        {isEditing && activeId === undefined && values.length > 0 && (
          <div className="mt-1 flex w-full flex-col">{renderEditBox(undefined)}</div>
        )}

        {isEditable && onSave && values.length > 0 && (!isEditing || activeId !== undefined) && (
          <div className="mt-0.5 flex items-center">
            <button
              type="button"
              onClick={() => handleEdit(undefined)}
              disabled={disabled || isSaving}
              className="flex items-center gap-1 px-2 py-1 text-xs font-normal leading-4 text-[#737373] disabled:opacity-50"
            >
              <Plus size={14} strokeWidth={2} className="shrink-0 text-[#737373]" />
              <span>Add</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiValueEditableField;